import * as React from "react";

import type {
  Activity,
  ActivityContentCommentAdded,
  ActivityContentGoalClosing,
  ActivityContentGoalDiscussionCreation,
  ActivityContentGoalReopening,
  ActivityContentGoalTimeframeEditing,
  ActivityContentProjectDiscussionSubmitted,
} from "@/api";
import type { ActivityHandler } from "../interfaces";

import * as Activities from "@/models/activities";
import { usePaths } from "@/routes/paths";
import { match } from "ts-pattern";
import { Link, Summary } from "turboui";
import { commentPath, commentedLink, feedTitle, goalLink, projectLink } from "../feedItemLinks";
import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";
import { parseCommentContent } from "@/models/comments";

const CommentAdded: ActivityHandler = {
  pageHtmlTitle(_activity: Activity) {
    throw new Error("Not implemented");
  },

  pagePath(paths, activity: Activity): string {
    const { activity: commentedActivity, comment } = content(activity);

    if (!commentedActivity) {
      return paths.homePath();
    }

    return match(commentedActivity.action)
      .with("goal_timeframe_editing", () => commentPath(paths.goalActivityPath(commentedActivity.id), comment))
      .with("goal_closing", () => commentPath(paths.goalActivityPath(commentedActivity.id), comment))
      .with("goal_discussion_creation", () => commentPath(paths.goalActivityPath(commentedActivity.id), comment))
      .with("goal_reopening", () => commentPath(paths.goalActivityPath(commentedActivity.id), comment))
      .with("project_discussion_submitted", () => {
        const projectSubmitted = commentedActivity.content as ActivityContentProjectDiscussionSubmitted;
        return commentPath(paths.projectDiscussionPath(projectSubmitted.discussion!.id!), comment);
      })
      .with("project_resuming", () => {
        return commentPath(paths.projectActivityPath(commentedActivity.id), comment);
      })
      .with("project_pausing", () => {
        return commentPath(paths.projectActivityPath(commentedActivity.id), comment);
      })
      .otherwise(() => {
        throw new Error("Comment added not implemented for action: " + commentedActivity.action);
      });
  },

  PageTitle(_props: { activity: any }) {
    throw new Error("Not implemented");
  },

  PageContent(_props: { activity: Activity }) {
    throw new Error("Not implemented");
  },

  PageOptions(_props: { activity: Activity }) {
    return null;
  },

  FeedItemTitle({ activity, page }: { activity: Activity; page: any }) {
    const paths = usePaths();
    const { activity: commentedActivity, comment } = content(activity);

    if (!commentedActivity) {
      return feedTitle(activity, "commented");
    }

    return match(commentedActivity.action)
      .with("goal_timeframe_editing", () => {
        const c = commentedActivity.content as ActivityContentGoalTimeframeEditing;
        const goal = c.goal!;
        const path = paths.goalActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        const activityLink = <Link to={path}>timeframe change</Link>;

        if (page === "goal") {
          return feedTitle(activity, action, "on the", activityLink);
        } else {
          return feedTitle(activity, action, "on the", activityLink, "in the", goalLink(goal), "goal");
        }
      })
      .with("goal_closing", () => {
        const c = commentedActivity.content as ActivityContentGoalClosing;
        const goal = c.goal!;
        const path = paths.goalActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        const activityLink = <Link to={path}>goal closing</Link>;

        if (page === "goal") {
          return feedTitle(activity, action, "on the", activityLink);
        } else {
          return feedTitle(activity, action, "on the", activityLink, "in the", goalLink(goal), "goal");
        }
      })
      .with("goal_discussion_creation", () => {
        const { goal } = commentedActivity.content as ActivityContentGoalDiscussionCreation;
        const path = paths.goalActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        let activityLink: any = "a discussion";

        if (commentedActivity.commentThread) {
          activityLink = <Link to={path}>{commentedActivity.commentThread.title}</Link>;
        }

        if (page === "goal") {
          return feedTitle(activity, action, "on", activityLink);
        } else {
          return feedTitle(activity, action, "on", activityLink, "in the", goalLink(goal), "goal");
        }
      })
      .with("goal_reopening", () => {
        const c = commentedActivity.content as ActivityContentGoalReopening;
        const goal = c.goal!;
        const path = paths.goalActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        const activityLink = <Link to={path}>goal reopening</Link>;

        if (page === "goal") {
          return feedTitle(activity, action, "on the", activityLink);
        } else {
          return feedTitle(activity, action, "on the", activityLink, "in the", goalLink(goal), "goal");
        }
      })
      .with("project_discussion_submitted", () => {
        const { discussion, title, project } = commentedActivity.content as ActivityContentProjectDiscussionSubmitted;
        const path = discussion?.id ? paths.projectDiscussionPath(discussion.id) : null;
        const action = path ? commentedLink(path, comment) : "commented";
        let activityLink: any = "a discussion";

        if (path && (title || discussion?.title)) {
          activityLink = <Link to={path}>{title || discussion?.title}</Link>;
        }

        if (page === "project") {
          return feedTitle(activity, action, "on", activityLink);
        } else {
          return feedTitle(activity, action, "on", activityLink, "in the", projectLink(project), "project");
        }
      })
      .with("project_resuming", () => {
        const path = paths.projectActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        const activityLink = <Link to={path}>project resuming</Link>;
        const project = Activities.getProject(commentedActivity);

        if (page === "project") {
          return feedTitle(activity, action, "on", activityLink);
        } else {
          return feedTitle(activity, action, "on", activityLink, "in the", projectLink(project), "project");
        }
      })
      .with("project_pausing", () => {
        const path = paths.projectActivityPath(commentedActivity.id);
        const action = commentedLink(path, comment);
        const activityLink = <Link to={path}>project pausing</Link>;
        const project = Activities.getProject(commentedActivity);

        if (page === "project") {
          return feedTitle(activity, action, "on", activityLink);
        } else {
          return feedTitle(activity, action, "on", activityLink, "in the", projectLink(project), "project");
        }
      })
      .otherwise(() => {
        throw new Error("Comment added not implemented for action: " + commentedActivity.action);
      });
  },

  FeedItemContent({ activity }: { activity: Activity }) {
    const { comment } = content(activity);
    const { mentionedPersonLookup } = useRichEditorHandlers();
    const commentContent = parseCommentContent(comment?.content);

    if (!commentContent) {
      return null;
    }

    return <Summary content={commentContent} characterCount={200} mentionedPersonLookup={mentionedPersonLookup} />;
  },

  feedItemAlignment(_activity: Activity): "items-start" | "items-center" {
    return "items-start";
  },

  commentCount(_activity: Activity): number {
    throw new Error("Not implemented");
  },

  hasComments(_activity: Activity): boolean {
    throw new Error("Not implemented");
  },

  NotificationTitle({ activity }: { activity: Activity }) {
    const commentedActivity = content(activity).activity!;
    const action = match(commentedActivity.action)
      .with("goal_timeframe_editing", () => "timeframe change")
      .with("goal_closing", () => "goal closing")
      .with("goal_discussion_creation", () => commentedActivity.commentThread!.title)
      .with("goal_reopening", () => "goal reopening")
      .with("project_discussion_submitted", () => commentedActivity.commentThread!.title)
      .with("project_resuming", () => "project resuming")
      .with("project_pausing", () => "project pausing")
      .otherwise(() => {
        throw new Error("Comment added not implemented for action: " + commentedActivity.action);
      });

    return "Re: " + action;
  },

  NotificationLocation({ activity }: { activity: Activity }) {
    const commentedActivity = content(activity).activity!;

    return match(commentedActivity.action)
      .with("goal_timeframe_editing", () => {
        const c = commentedActivity.content as ActivityContentGoalTimeframeEditing;
        return c.goal!.name!;
      })
      .with("goal_closing", () => {
        const c = commentedActivity.content as ActivityContentGoalClosing;
        return c.goal!.name!;
      })
      .with("goal_discussion_creation", () => {
        const c = commentedActivity.content as ActivityContentGoalDiscussionCreation;
        return c.goal!.name!;
      })
      .with("goal_reopening", () => {
        const c = commentedActivity.content as ActivityContentGoalReopening;
        return c.goal!.name!;
      })
      .with("project_discussion_submitted", () => {
        const c = commentedActivity.content as ActivityContentProjectDiscussionSubmitted;
        return c.title!;
      })
      .with("project_resuming", () => {
        const project = Activities.getProject(commentedActivity);
        return project.name!;
      })
      .with("project_pausing", () => {
        const project = Activities.getProject(commentedActivity);
        return project.name!;
      })
      .otherwise(() => {
        throw new Error("Comment added not implemented for action: " + commentedActivity.action);
      });
  },
};

function content(activity: Activity): ActivityContentCommentAdded {
  return activity.content as ActivityContentCommentAdded;
}

export default CommentAdded;
